import { useEffect, useRef, useState } from 'react'
import HttpService from '../services/HttpService'
import { Button, Card, Field, Modal, Page, inputClass } from './ui'

export default function Configuration(props) {
    const [codigoEmpresa, setCodigoEmpresa] = useState('')
    const [rfc, setRfc] = useState('')
    const [razonSocial, setRazonSocial] = useState('')
    const [aliasCertificado, setAliasCertificado] = useState('')
    const [dv, setDv] = useState('')
    const [cifrado, setCifrado] = useState('')
    const [validateRfcError, setValidateRfcError] = useState('')
    const [validateDvError, setValidateDvError] = useState('')
    const [validateAliasCertificadoError, setValidateAliasCertificadoError] = useState('')
    const [validateRazonSocialError, setValidateRazonSocialError] = useState('')
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [saving, setSaving] = useState(false)
    const [message, setMessage] = useState('')
    const [saveError, setSaveError] = useState('')
    const validForm = useRef(true)

    useEffect(() => {
        props.handleTitulo('Configuración')
        getEmpresa()
    }, [])

    const isRfc = (rfc) =>
        /^[A-Z&Ñ]{3,4}[0-9]{2}(0[1-9]|1[012])(0[1-9]|[12][0-9]|3[01])[A-Z0-9]{2}[0-9A]$/i.test(rfc)

    const getEmpresa = () => {
        HttpService.getAxiosClient().get('/empresa').then((res) => {
            setCodigoEmpresa(res.data.codigoEmpresa)
            setRfc(res.data.rfc)
            setRazonSocial(res.data.razonSocial)
            setAliasCertificado(res.data.aliasCertificado)
            setDv(res.data.dv)
            setCifrado(res.data.cifrado)
        })
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        setMessage('')
        setSaveError('')
        validateForm()
        if (validForm.current) setConfirmOpen(true)
    }

    const guardarCambios = () => {
        setSaving(true)
        setSaveError('')
        HttpService.getAxiosClient()
            .post(`/empresas/${codigoEmpresa}`, {
                rfc,
                razonSocial,
                alias: aliasCertificado,
                dv,
                cifrado,
            })
            .then(() => {
                setConfirmOpen(false)
                setMessage('Los datos de operación CoDi se actualizaron correctamente.')
            })
            .catch((err) => {
                const apiMessage = err.response?.data?.message || err.response?.data?.error
                setSaveError(typeof apiMessage === 'string' ? apiMessage : 'No se pudieron guardar los cambios. Inténtalo de nuevo.')
            })
            .finally(() => setSaving(false))
    }

    const validateForm = () => {
        validForm.current = true
        if (!isRfc(rfc)) {
            setValidateRfcError('Debe ingresar un RFC válido')
            validForm.current = false
        } else {
            setValidateRfcError(null)
        }
        if (!razonSocial?.trim()) {
            setValidateRazonSocialError('Debe ingresar la razón social')
            validForm.current = false
        } else {
            setValidateRazonSocialError(null)
        }
        if (isNaN(dv)) {
            setValidateDvError('Debe tener un valor numérico')
            validForm.current = false
        } else {
            setValidateDvError(null)
        }
        if (isNaN(aliasCertificado) || (aliasCertificado?.trim() && aliasCertificado?.length !== 20)) {
            setValidateAliasCertificadoError('Debe estar formado por 20 dígitos')
            validForm.current = false
        } else {
            setValidateAliasCertificadoError(null)
        }
    }

    return (
        <Page description="Datos de la empresa para operar en CoDi.">
            <Card className="max-w-3xl p-6">
                <h3 className="text-base font-semibold text-slate-900">Datos de la empresa</h3>
                <p className="mt-1 text-sm text-amber-800">
                    Esta información es necesaria para operar. Un dato incorrecto puede dejar de procesar transacciones.
                </p>
                {codigoEmpresa && (
                    <p className="mt-3 text-xs text-slate-500">
                        Código de empresa: <span className="font-mono text-slate-800">{codigoEmpresa}</span>
                    </p>
                )}
                <form onSubmit={handleSubmit} className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-6">
                    <fieldset disabled={saving} className="contents">
                        <div className="sm:col-span-2">
                            <Field label="RFC" error={validateRfcError}>
                                <input type="text" value={rfc} onChange={(e) => setRfc(e.target.value)} className={inputClass} />
                            </Field>
                        </div>
                        <div className="sm:col-span-4">
                            <Field
                                label="Razón social"
                                hint="La asociada a la e.firma usada en el registro de Banxico"
                                error={validateRazonSocialError}
                            >
                                <input type="text" value={razonSocial} onChange={(e) => setRazonSocial(e.target.value)} className={inputClass} />
                            </Field>
                        </div>
                        <div className="sm:col-span-4">
                            <Field label="Alias de certificado" error={validateAliasCertificadoError}>
                                <input
                                    type="text"
                                    maxLength={20}
                                    value={aliasCertificado}
                                    onChange={(e) => setAliasCertificado(e.target.value)}
                                    className={inputClass}
                                />
                            </Field>
                        </div>
                        <div className="sm:col-span-2">
                            <Field label="DV" error={validateDvError}>
                                <input type="text" maxLength={9} value={dv} onChange={(e) => setDv(e.target.value)} className={inputClass} />
                            </Field>
                        </div>
                        <div className="sm:col-span-6">
                            <Field label="Clave de cifrado">
                                <input type="text" value={cifrado} onChange={(e) => setCifrado(e.target.value)} className={inputClass} />
                            </Field>
                        </div>
                    </fieldset>
                    {message && (
                        <div className="sm:col-span-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                            {message}
                        </div>
                    )}
                    {saveError && (
                        <div className="sm:col-span-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                            {saveError}
                        </div>
                    )}
                    <div className="sm:col-span-6 flex justify-end">
                        <Button type="submit" disabled={saving}>
                            {saving ? 'Guardando…' : 'Guardar cambios'}
                        </Button>
                    </div>
                </form>
            </Card>

            <Modal
                open={confirmOpen}
                onClose={() => !saving && setConfirmOpen(false)}
                title="Confirmar cambios"
                footer={
                    <>
                        <Button variant="secondary" disabled={saving} onClick={() => setConfirmOpen(false)}>
                            Cancelar
                        </Button>
                        <Button disabled={saving} onClick={guardarCambios}>
                            {saving ? (
                                <span className="inline-flex items-center gap-2">
                                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                    </svg>
                                    Guardando…
                                </span>
                            ) : (
                                'Sí, guardar'
                            )}
                        </Button>
                    </>
                }
            >
                <p className="text-sm text-slate-600">
                    ¿Estás seguro de guardar? Confirma que ya validaste la información que estás cambiando. Un error puede impedir operar CoDi.
                </p>
            </Modal>
        </Page>
    )
}
